import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { authedFetch, hasStoredSession, useStoredAuth, type StoredAuth } from "nazare-ui/utils";
import { subscribeAuthenticatedSse } from "./authenticated-sse";
import { resolveBffDomain } from "./bff-domain";

/**
 * Real BFF client for QA Automation — same `/qa-automation/*` REST contract
 * the reference app's `app/web/app/screens/admin/qa-automation/use-qa-runs.ts`
 * consumes (see that file, and its sibling `schemas.ts` for the authoritative
 * Zod contract this file's types are hand-copied from). `platform/` has no
 * Apollo/`afetch` context of its own — like the notification bell
 * (`nazare-ui/utils`'s `authedFetch`), this reads whatever nazare-frontend
 * session already sits in `localStorage` and talks to the BFF directly.
 *
 * Types below are hand-typed from `schemas.ts`, not re-derived from its Zod
 * schemas at build time (no cross-app import boundary exists for that) — if
 * the BFF contract changes, this drifts silently. Matches this repo's own
 * precedent (`use-notifications.ts` does the same, no zod).
 */

export type Verdict =
  | "pass"
  | "pass_with_sub_finding"
  | "fail_regression"
  | "fail_caught_tweak"
  | "flag_ux"
  | "na_scope"
  | "unknown";

export type EnvironmentLabel = "dev" | "qa" | "prod" | "local" | "other";
export type UserSource = "storage-state" | "qa-bot" | "no-auth" | "unknown";
export type RunLifecycleStatus = "queued" | "running" | "passed" | "failed" | "abandoned";

export type RunCi = {
  provider: "github" | "local";
  runUrl: string | null;
  sha: string | null;
  branch: string | null;
  actor: string | null;
};

export type RunEnvironment = { baseUrl: string; label: EnvironmentLabel };
export type RunUser = { email: string | null; source: UserSource };

export type RunCounts = {
  pass: number;
  passWithSubFinding: number;
  failRegression: number;
  naScope: number;
  total: number;
};

export type TokenTotals = { input: number; output: number };
export type TestTokens = TokenTotals & { cacheRead: number; cacheWrite: number };

export type RunSummary = {
  schemaVersion: 1;
  runId: string;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  project: string;
  environment: RunEnvironment;
  user: RunUser;
  ci: RunCi;
  counts: RunCounts;
  status: RunLifecycleStatus;
  costUsd: number | null;
  tokens: TokenTotals | null;
};

export type RunListResponse = { runs: RunSummary[]; nextCursor: string | null };

export type QaSchedule = {
  enabled: boolean;
  cron: string | null;
  timezone: string;
  nextRuns: string[];
};

export type QaActiveRun = {
  runId: string;
  status: "queued" | "running";
  triggerSource: "dashboard" | "scheduled";
  projects: string[] | null;
  grep: string | null;
  queuedAt: string;
  startedAt: string | null;
  user: { email: string };
};

export type TestHistoryRun = {
  runId: string;
  date: string;
  startedAt: string;
  project: string;
  verdict: Verdict;
  summary: string | null;
  errorMessage: string | null;
  durationMs: number;
  attempts: number;
  videoKey: string | null;
  videoUrl: string | null;
};

export type TestHistoryResponse = TitleDisplay & {
  title: string;
  window: string;
  runs: TestHistoryRun[];
};

export type StartQaRunRequest = { projects?: string[]; grep?: string };
export type StartQaRunResponse = { runId: string; position: number };

export type QaRunStreamEvent =
  | { type: "queued" }
  | { type: "started" }
  | { type: "enumerated"; tests: Array<{ promptId: string | null; title: string; project: string; titlePath?: string[] }> }
  | { type: "test_started"; promptId: string | null; title: string; project: string; startedAt: string; titlePath?: string[] }
  | { type: "test_finished"; promptId: string | null; title: string; project: string; verdict: Verdict; titlePath?: string[]; tokens?: TokenTotals }
  | { type: "screencast_frame"; project: string; title: string; data: string; ts?: number }
  | { type: "run_finished"; status: "passed" | "failed" | "abandoned"; summary: { passed: number; warned: number; failed: number; total: number } };

export type TitleDisplay = { titlePath: string[]; primary: string; context: string };

export type RunTestRowDisplay = TitleDisplay & {
  id: string;
  title: string;
  project: string;
  verdict: Verdict | null;
  summary: string | null;
  promptText: string | null;
  errorMessage: string | null;
  durationMs: number;
  attempts: number;
  videoKey: string | null;
  tokens: TestTokens | null;
  tokensByModel: Record<string, TestTokens> | null;
  costUsd: number | null;
};

export type TreeAggregate = {
  pass: number;
  passWithSubFinding: number;
  failRegression: number;
  naScope: number;
  total: number;
  durationMs: number;
  tokens: TokenTotals | null;
  costUsd: number | null;
};

export type TestTreeNode = {
  name: string;
  path: string[];
  testIds: string[];
  children: TestTreeNode[];
  aggregate: TreeAggregate;
};

export type PresignedVideo = { testId: string; url: string; expiresAt: string };

export type ModelPrice = {
  inputPerMTok: number;
  outputPerMTok: number;
  cacheReadPerMTok: number | null;
  cacheWritePerMTok: number | null;
};

export type RunDetailResponse = {
  schemaVersion: 1;
  runId: string;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  project: string;
  environment: RunEnvironment;
  user: RunUser;
  ci: RunCi;
  counts: RunCounts;
  status?: RunLifecycleStatus;
  tests: RunTestRowDisplay[];
  tree: TestTreeNode;
  presigned: {
    reportZipUrl: string;
    failureVideosZipUrl: string | null;
    videos: PresignedVideo[];
    expiresAt: string;
  };
  costUsd: number | null;
  tokens: TokenTotals | null;
  modelPrices: Record<string, ModelPrice>;
};

export type AggregateWindow = "today" | "7d" | "30d" | "6m" | "all";

export type AggregateBucket = {
  date: string;
  pass: number;
  passWithSubFinding: number;
  failRegression: number;
  naScope: number;
  runs: number;
  avgDurationMs: number;
};

export type PerTestDuration = TitleDisplay & { title: string; avgMs: number; runs: number };
export type PerTestFailureRate = TitleDisplay & {
  title: string;
  failPct: number;
  failCount: number;
  runs: number;
};

export type AggregateResponse = {
  window: AggregateWindow;
  generatedAt: string;
  overall: { runs: number; passPct: number; failPct: number; avgDurationMs: number };
  buckets: AggregateBucket[];
  perTestAvgDurationMs: PerTestDuration[];
  perTestFailureRate: PerTestFailureRate[];
};

// Mirrors the reference app's own `getServiceDomain("bff")` (env-var lookup
// + `/bff` suffix when no dedicated BFF domain is set) — platform has no
// `getEnv()` wrapper of its own, so this reads `import.meta.env` directly,
// same as `nazare-ui/utils`'s notification-feed base-URL lookup.
const QA_AUTOMATION_BASE = `${resolveBffDomain()}/qa-automation`;

export { SessionExpiredError, hasStoredSession } from "nazare-ui/utils";

// ─────────────────────────────────────────────────────────────────────────
// Shared formatting — every QA Automation page (dashboard card, maximised
// list, run detail) renders the same `RunSummary`/duration/cost/token
// shapes, so these live in one place instead of being redefined per file.
// ─────────────────────────────────────────────────────────────────────────

export function formatRelativeToNow(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffSeconds = Math.round((then - Date.now()) / 1000);
  const abs = Math.abs(diffSeconds);
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  if (abs < 60) return rtf.format(diffSeconds, "second");
  if (abs < 3600) return rtf.format(Math.round(diffSeconds / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diffSeconds / 3600), "hour");
  return rtf.format(Math.round(diffSeconds / 86400), "day");
}

export function formatDurationMs(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}

export function formatCostUsd(usd: number | null): string {
  return usd == null ? "est. n/a" : `~$${usd.toFixed(2)}`;
}

export function formatTokens(count: number | undefined | null): string {
  if (count == null) return "0";
  return count >= 1000 ? `${(count / 1000).toFixed(1)}k` : `${count}`;
}

async function fetchQaRunsList(
  auth: StoredAuth | null,
  params: { from?: string; to?: string; project?: string; cursor?: string; limit?: number },
): Promise<RunListResponse> {
  const qs = new URLSearchParams();
  if (params.from) qs.append("from", params.from);
  if (params.to) qs.append("to", params.to);
  if (params.project) qs.append("project", params.project);
  // `cursor`/`limit` — the BFF's `/runs` route (`routes/list-runs.ts`) is
  // simple offset-based pagination wearing a `cursor` name: it echoes back
  // `nextCursor: String(offset + limit)` when a full page came back, `null`
  // once a short page proves there's nothing left. Passed through untouched
  // here so `useQaRunsInfiniteList` below can drive it via
  // `useInfiniteQuery`.
  if (params.cursor) qs.append("cursor", params.cursor);
  if (params.limit != null) qs.append("limit", String(params.limit));
  const query = qs.toString();
  const res = await authedFetch(QA_AUTOMATION_BASE, auth, `/runs${query ? `?${query}` : ""}`);
  if (!res.ok) throw new Error(`Failed to fetch QA runs: ${res.status}`);
  return res.json();
}

/** Shared queryKey prefix for the dashboard card's unpaginated
 * `useQaRunsList` — factored out so `useQaRunsInfiniteList` below can look
 * this exact key up in the cache to seed its own first page, without the two
 * key-building sites silently drifting apart. */
function qaRunsListQueryKey(params: { from?: string; to?: string; project?: string }) {
  return ["qa-automation", "runs-list", params.from ?? null, params.to ?? null, params.project ?? null] as const;
}

/** Real "Recent runs" data for the dashboard card (`recent-runs.tsx`) —
 * fetches a single unpaginated window (the card only ever shows a handful of
 * rows). The maximised page (`pages/recent-runs.tsx`) uses
 * `useQaRunsInfiniteList` below instead, since it needs to keep paging. */
export function useQaRunsList(params: { from?: string; to?: string; project?: string } = {}) {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: qaRunsListQueryKey(params),
    queryFn: () => fetchQaRunsList(auth, params),
    enabled: hasStoredSession(auth),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

/** Environment-backed QA cadence. This endpoint is intentionally read-only:
 * changes belong to the deployment configuration, not an in-app toggle. */
export function useQaSchedule() {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: ["qa-automation", "schedule"],
    queryFn: async (): Promise<QaSchedule> => {
      const res = await authedFetch(QA_AUTOMATION_BASE, auth, "/schedule");
      if (!res.ok) throw new Error(`Failed to fetch QA schedule: ${res.status}`);
      return res.json();
    },
    enabled: hasStoredSession(auth),
    staleTime: 5 * 60_000,
  });
}

export function useQaActiveRuns() {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: ["qa-automation", "active"],
    queryFn: async (): Promise<{ runs: QaActiveRun[] }> => {
      const res = await authedFetch(QA_AUTOMATION_BASE, auth, "/active");
      if (!res.ok) throw new Error(`Failed to fetch active QA runs: ${res.status}`);
      return res.json();
    },
    enabled: hasStoredSession(auth),
    refetchInterval: 8_000,
    placeholderData: keepPreviousData,
  });
}

/** Keep dashboard/list caches current from each active run's SSE endpoint. */
export function useQaRunsLiveSync(activeRuns: QaActiveRun[]) {
  const auth = useStoredAuth();
  const client = useQueryClient();
  const idsKey = activeRuns.map((run) => run.runId).sort().join(",");
  useEffect(() => {
    if (!hasStoredSession(auth) || !idsKey) return;
    const unsubscribers = idsKey.split(",").map((runId) => subscribeAuthenticatedSse({
      baseUrl: QA_AUTOMATION_BASE,
      auth,
      path: `/runs/${encodeURIComponent(runId)}/stream`,
      onMessage: ({ data }) => {
        try {
          const event = JSON.parse(data) as QaRunStreamEvent;
          if (event.type === "screencast_frame") return;
          if (event.type === "test_started" || event.type === "test_finished" || event.type === "enumerated") {
            void client.invalidateQueries({ queryKey: ["qa-automation", "run-detail", runId] });
          }
          if (event.type === "queued" || event.type === "started" || event.type === "run_finished") {
            void client.invalidateQueries({ queryKey: ["qa-automation", "active"] });
            void client.invalidateQueries({ queryKey: ["qa-automation", "runs-list"] });
          }
          if (event.type === "run_finished") {
            void client.invalidateQueries({ queryKey: ["qa-automation", "aggregates"] });
          }
        } catch { /* Ignore malformed frames. */ }
      },
    }));
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [auth, client, idsKey]);
}

export function useStartQaRun() {
  const auth = useStoredAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: StartQaRunRequest): Promise<StartQaRunResponse> => {
      const res = await authedFetch(QA_AUTOMATION_BASE, auth, "/runs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error(`Failed to start QA run: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["qa-automation", "active"] });
      void queryClient.invalidateQueries({ queryKey: ["qa-automation", "runs-list"] });
    },
  });
}

/** Real, cursor-paginated "Recent runs" data — powers the maximised
 * `pages/recent-runs.tsx` table's infinite scroll and calls the BFF for each
 * page via `fetchQaRunsList`'s `cursor`/`limit` params.
 *
 * Seeded from the dashboard card's own `useQaRunsList` cache entry when one
 * exists (same `from`/`to`/`project`, `queryFn` untouched — the shapes match
 * exactly since both hit `fetchQaRunsList`): landing on `/qa-automation/
 * recent-runs` a click after the dashboard card already loaded shows that
 * card's rows INSTANTLY (no blank flash, no skeleton-from-scratch) via
 * `initialData`, while `initialDataUpdatedAt: 0` forces this query to still
 * treat that seed as stale and kick off its own real (larger, paginated)
 * background fetch — the loading bar in `pages/recent-runs.tsx` is that
 * fetch's `isFetching` state. Navigating here with nothing cached yet (the
 * dashboard card was never mounted this session) falls back to the normal
 * `isLoading` skeleton, since `initialData` is `undefined` in that case. */
export function useQaRunsInfiniteList(
  params: { from?: string; to?: string; project?: string; pageSize?: number } = {},
) {
  const auth = useStoredAuth();
  const queryClient = useQueryClient();
  const pageSize = params.pageSize ?? 50;
  return useInfiniteQuery({
    queryKey: [
      "qa-automation",
      "runs-list-infinite",
      params.from ?? null,
      params.to ?? null,
      params.project ?? null,
      pageSize,
    ],
    queryFn: ({ pageParam }) =>
      fetchQaRunsList(auth, {
        from: params.from,
        to: params.to,
        project: params.project,
        cursor: pageParam,
        limit: pageSize,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: hasStoredSession(auth),
    staleTime: 60_000,
    initialData: () => {
      const seed = queryClient.getQueryData<RunListResponse>(qaRunsListQueryKey(params));
      if (!seed) return undefined;
      return { pages: [seed], pageParams: [undefined] };
    },
    initialDataUpdatedAt: 0,
  });
}

async function fetchQaRunDetail(auth: StoredAuth | null, runId: string): Promise<RunDetailResponse> {
  const res = await authedFetch(QA_AUTOMATION_BASE, auth, `/runs/${runId}`);
  if (!res.ok) throw new Error(`Failed to fetch QA run ${runId}: ${res.status}`);
  return res.json();
}

/** Real single-run detail — powers `pages/run-detail.tsx`, including the
 * real test tree, prompts/results, and presigned video URLs. */
export function useQaRunDetail(runId: string) {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: ["qa-automation", "run-detail", runId],
    queryFn: () => fetchQaRunDetail(auth, runId),
    enabled: hasStoredSession(auth) && !!runId,
    refetchInterval: (query) => (query.state.data?.status === "running" ? 5000 : false),
  });
}

/** Subscribe to a run's full live event feed, including screencast frames. */
export function useQaRunStream(runId: string, enabled = true) {
  const auth = useStoredAuth();
  const client = useQueryClient();
  const [event, setEvent] = useState<QaRunStreamEvent | null>(null);
  const [frame, setFrame] = useState<Extract<QaRunStreamEvent, { type: "screencast_frame" }> | null>(null);
  const invalidateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    setEvent(null);
    setFrame(null);
    if (!enabled || !hasStoredSession(auth) || !runId) return;
    return subscribeAuthenticatedSse({
      baseUrl: QA_AUTOMATION_BASE,
      auth,
      path: `/runs/${encodeURIComponent(runId)}/stream`,
      onMessage: ({ data }) => {
        try {
          const next = JSON.parse(data) as QaRunStreamEvent;
          setEvent(next);
          if (next.type === "screencast_frame") setFrame(next);
          if (next.type === "run_finished") setFrame(null);
          if (next.type !== "screencast_frame") {
            if (invalidateTimer.current) clearTimeout(invalidateTimer.current);
            invalidateTimer.current = setTimeout(() => {
              void client.invalidateQueries({ queryKey: ["qa-automation", "run-detail", runId] });
              if (next.type === "run_finished") {
                void client.invalidateQueries({ queryKey: ["qa-automation", "active"] });
                void client.invalidateQueries({ queryKey: ["qa-automation", "runs-list"] });
                void client.invalidateQueries({ queryKey: ["qa-automation", "aggregates"] });
              }
            }, 150);
          }
        } catch { /* Ignore malformed frames. */ }
      },
    });
  }, [auth, client, enabled, runId]);
  useEffect(() => () => {
    if (invalidateTimer.current) clearTimeout(invalidateTimer.current);
  }, []);
  return { event, frame };
}

async function fetchQaAggregates(auth: StoredAuth | null, window: AggregateWindow): Promise<AggregateResponse> {
  const res = await authedFetch(QA_AUTOMATION_BASE, auth, `/aggregates?window=${window}`);
  if (!res.ok) throw new Error(`Failed to fetch QA aggregates: ${res.status}`);
  return res.json();
}

/** Real dashboard aggregates — verdict trend, average duration, "5 slowest
 * tests" and "tests with the highest failure rate". */
export function useQaAggregates(window: AggregateWindow) {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: ["qa-automation", "aggregates", window],
    queryFn: () => fetchQaAggregates(auth, window),
    enabled: hasStoredSession(auth),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

export function useQaTestHistory(title: string, window: AggregateWindow, enabled = true) {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: ["qa-automation", "test-history", title, window],
    enabled: hasStoredSession(auth) && enabled && !!title,
    queryFn: async (): Promise<TestHistoryResponse> => {
      const params = new URLSearchParams({ title, window });
      const res = await authedFetch(QA_AUTOMATION_BASE, auth, `/test-history?${params}`);
      if (!res.ok) throw new Error(`Failed to fetch QA test history: ${res.status}`);
      return res.json();
    },
    staleTime: 60_000,
  });
}

/**
 * Fetches a per-test recording as an authenticated blob and hands back a
 * `File` — a bare `<video src>` can't attach the Bearer header, so this
 * mirrors the reference app's own `TestVideo` component (`afetchRaw`), just
 * via `authedFetch` instead of Apollo's `afetchRaw`. Returns a `File`
 * (rather than a bare object URL) so it can be passed straight into
 * `nazare-ui/components/preview`'s `VideoPreview`/`FilePreview`, which
 * dispatch on `File.type`.
 */
export async function fetchQaTestVideoFile(
  auth: StoredAuth | null,
  videoPath: string,
  testId: string,
): Promise<File> {
  const res = await authedFetch(resolveBffDomain(), auth, videoPath);
  if (!res.ok) throw new Error(`Failed to fetch test recording: ${res.status}`);
  const blob = await res.blob();
  return new File([blob], `${testId}.webm`, { type: blob.type || "video/webm" });
}
